<template>
  <b-modal
    ref="modal"
    :visible="true"
    static
    lazy
    centered
    no-fade
    size="lg"
    :title="title"
    hide-footer
    @hide="onHide"
    @hidden="onHidden"
  >
    <form @submit.prevent="submit" novalidate>
      <div class="form-group">
        <label class="small font-weight-bold text-muted" for="wtf-title">Название работы <span class="text-danger">*</span></label>
        <input id="wtf-title" v-model="form.title" type="text" class="form-control form-control-sm wtf__title" :disabled="busy" maxlength="255">
      </div>

      <div class="form-group">
        <label class="small font-weight-bold text-muted" for="wtf-add">Материалы из прайс‑листа</label>
        <select id="wtf-add" class="form-control form-control-sm wtf__add" :disabled="busy || available.length === 0" @change="addFromSelect">
          <option value="">{{ addPlaceholder }}</option>
          <option v-for="c in available" :key="c.id" :value="c.id">{{ c.title }}</option>
        </select>
      </div>

      <div v-if="form.items.length" class="wtf__items">
        <div v-for="(item, index) in form.items" :key="item.id" class="wtf__item">
          <div class="wtf__item-main">
            <div class="wtf__item-title">{{ item.title }}<span v-if="item.archived" class="wtf__tag">архив</span></div>
            <div class="wtf__item-line">{{ itemLine(item) }}</div>
          </div>
          <input v-model="item.quantity" type="number" min="1" step="1" class="form-control form-control-sm wtf__qty" :disabled="busy" aria-label="Количество">
          <button type="button" class="btn btn-sm btn-light wtf__remove" :disabled="busy" aria-label="Убрать материал" @click="removeItem(index)">×</button>
        </div>
      </div>

      <div class="form-group">
        <label class="small font-weight-bold text-muted" for="wtf-price">Стоимость работ без учёта расходников, ₽ <span class="text-danger">*</span></label>
        <input id="wtf-price" v-model="form.price" type="number" min="0" step="0.01" class="form-control form-control-sm wtf__price" :disabled="busy">
      </div>

      <div class="wtf__tiles">
        <div class="wtf__tile">
          <div class="wtf__tile-label">Расходники</div>
          <div class="wtf__tile-value">{{ rub(money.consumablesTotal) }}</div>
          <div v-if="money.incomplete" class="wtf__tile-hint">не все цены заданы</div>
        </div>
        <div class="wtf__tile">
          <div class="wtf__tile-label">Работы</div>
          <div class="wtf__tile-value">{{ rub(money.workPrice) }}</div>
        </div>
        <div class="wtf__tile wtf__tile--total">
          <div class="wtf__tile-label">Итог</div>
          <div class="wtf__tile-value">{{ rub(money.total) }}</div>
        </div>
      </div>

      <ul v-if="errors.length" class="alert alert-danger py-2 pl-4 mb-3" role="alert">
        <li v-for="(e, i) in errors" :key="i">{{ e }}</li>
      </ul>

      <div class="d-flex justify-content-between">
        <button type="button" class="btn btn-secondary btn-sm wtf__cancel" :disabled="busy" @click="hideModal">Отмена</button>
        <button type="submit" class="btn btn-success btn-sm wtf__submit" :disabled="busy">
          <span v-if="busy" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>Сохранить
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import * as api from '@/components/management/dispatcher/api';
import {
  emptyWorkTypeForm, workTypeFormFromRow, itemFromConsumable, availableConsumables, formMoney,
  validateWorkTypeForm, buildWorkTypePayload, formatRub, lineTotal,
} from '@/components/management/dispatcher/catalogPayload';
import { notifySuccess, notifyError } from '@/components/management/dispatcher/notify';
import modalCloseMixin from '@/components/management/dispatcher/map/modalCloseMixin';

export default {
  name: 'WorkTypeFormModal',
  mixins: [modalCloseMixin],
  props: {
    row: { type: Object, default: null },
    consumables: { type: Array, default: () => [] },
  },
  data() {
    return {
      form: this.row ? workTypeFormFromRow(this.row) : emptyWorkTypeForm(),
      errors: [],
      busy: false,
    };
  },
  computed: {
    title() { return this.row ? 'Изменение работы' : 'Новая работа'; },
    available() { return availableConsumables(this.consumables, this.form.items); },
    addPlaceholder() {
      if (this.available.length) return '— добавить материал —';
      return this.consumables.length ? '— все материалы добавлены —' : '— нет активных материалов —';
    },
    money() { return formMoney(this.form); },
  },
  methods: {
    rub: (n) => formatRub(n),
    itemLine(item) {
      return `${formatRub(item.unitPrice)} × ${item.quantity || 0} = ${formatRub(lineTotal(item.unitPrice, item.quantity))}`;
    },
    addFromSelect(event) {
      const id = Number(event.target.value);
      event.target.value = '';
      if (!id) return;
      const consumable = this.consumables.find((c) => Number(c.id) === id);
      if (!consumable || this.form.items.some((i) => i.id === id)) return;
      this.form.items.push(itemFromConsumable(consumable));
    },
    removeItem(index) {
      this.form.items.splice(index, 1);
    },
    async submit() {
      if (this.busy) return;
      this.errors = validateWorkTypeForm(this.form);
      if (this.errors.length) return;
      this.busy = true;
      try {
        const payload = buildWorkTypePayload(this.form, this.row);
        const saved = this.row ? await api.updateWorkType(payload) : await api.createWorkType(payload);
        notifySuccess(this, 'Вид работ сохранён');
        this.$emit('saved', saved);
        this.hideModal();
      } catch (e) {
        notifyError(this, e);
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.wtf__items { border: 1px solid #e3e5e1; border-radius: 8px; margin-bottom: 16px; }
.wtf__item { display: flex; align-items: center; gap: 10px; padding: 8px 12px; & + & { border-top: 1px solid #eef0ec; } }
.wtf__item-main { flex: 1; min-width: 0; }
.wtf__item-title { font-size: 13px; font-weight: 600; color: #2f3431; }
.wtf__item-line { font-size: 12px; color: #8b918c; }
.wtf__qty { width: 80px; flex: none; text-align: center; }
.wtf__remove { flex: none; line-height: 1; }
.wtf__tag { display: inline-block; margin-left: 8px; padding: 1px 6px; border-radius: 6px; background: #f4f5f1; color: #8b918c; font-size: 10.5px; text-transform: uppercase; letter-spacing: .06em; font-weight: 600; }
.wtf__tiles { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 16px; }
.wtf__tile { border: 1px solid #e3e5e1; border-radius: 8px; padding: 8px 12px; background: #fff; }
.wtf__tile--total { background: #f2f9f6; border-color: #cfe7de; }
.wtf__tile-label { font-size: 10.5px; letter-spacing: .08em; text-transform: uppercase; color: #9aa09b; }
.wtf__tile-value { font-size: 15px; font-weight: 700; color: #2f3431; .wtf__tile--total & { color: #0f5a4a; } }
.wtf__tile-hint { font-size: 11px; color: #8b918c; }
</style>
